import axios from 'axios';

const downloadFile = (response, filename = 'vragenlijst.pdf') => {
  let contentType = response.headers['content-type'] || 'text/plain';
  let contentDisposition = response.headers['content-disposition'] || '';

  contentDisposition.split(';').forEach((setting) => {
    let entry = setting.split('=').map((s) => s.trim());
    if (entry[0] === 'filename') {
      filename = entry[1];
    }
  });

  let blob = new Blob([response.data], { type: contentType });
  let tag = document.createElement('a');

  if ('download' in tag && window.navigator.userAgent.indexOf('Edge') === -1) {
    tag.download = filename;
    let windowUrl = window.URL || window.webkitURL;
    let url = windowUrl.createObjectURL(blob);
    tag.href = url;
    tag.target = '_blank';
    document.body.appendChild(tag);
    tag.click();
    windowUrl.revokeObjectURL(url);
  } else {
    navigator.msSaveBlob(blob, filename);
  }
};

// Produces a URL for template language import / export with an optional language parameter
function getLanguageUrl(config, version, resource, language = null) {
  let url = `/version/${version.id}/translations/${resource}`;
  if (language) {
    url = `${url}?lang=${language.toLowerCase()}`;
  }

  return url;
}

export async function preflightExcelFile(version, b64Contents) {
  const response = await axios.post(
    `/version/${version.id}/translations/excel-preflight`,
    {
      contents: b64Contents,
    }
  );
  return response.data;
}

// Downloads an Excel template translations file
export async function exportTemplateTranslationsExcel(config, version) {
  const filename = `templates_${config.name}_v${version.version}.xlsx`;
  const url = getLanguageUrl(config, version, 'templates.xlsx');
  const response = await axios.get(url, { responseType: 'blob' });
  downloadFile(response, filename);
}

// Uploads template language contents
export function importTemplateLanguage(
  config,
  version,
  contents,
  language = null
) {
  const url = getLanguageUrl(config, version, 'templates', language);
  const payload = JSON.parse(contents);
  return axios.put(url, payload);
}

export function importTemplateLanguageExcel(
  config,
  version,
  filename,
  b64Contents,
  sheet,
  languages
) {
  const url = getLanguageUrl(config, version, 'templates.xlsx');
  const payload = {
    filename,
    contents: b64Contents,
    sheet,
    languages,
  };
  return axios.put(url, payload);
}

// Download an Excel questionnaire translation file
export async function exportQuestionnaireTranslationsExcel(config, version) {
  const filename = `questionnaire_${config.name}_v${version.version}.xlsx`;
  const url = getLanguageUrl(config, version, 'questionnaire.xlsx');
  const response = await axios.get(url, { responseType: 'blob' });
  downloadFile(response, filename);
}

export function importQuestionnaireLanguage(
  config,
  version,
  contents,
  language = null
) {
  const url = getLanguageUrl(config, version, 'questionnaire', language);
  const payload = JSON.parse(contents);
  return axios.put(url, payload);
}

export function importQuestionnaireLanguageExcel(
  config,
  version,
  filename,
  b64Contents,
  sheet,
  languages
) {
  const url = getLanguageUrl(config, version, 'questionnaire.xlsx');
  const payload = {
    filename,
    contents: b64Contents,
    sheet,
    languages,
  };
  return axios.put(url, payload);
}

export function getTenants(options = {}) {
  const params = {
    sort: options.sort || 'name',
  };

  if (options.id) {
    params.id = options.id;
  }

  if (options.name) {
    params.name = options.name;
  }

  return axios.get('/tenants', { params });
}

export function getTenant(id) {
  return axios.get('/tenants/' + id);
}

export function createTenant(data) {
  return axios.post('/tenants', data);
}

export function updateTenant(data) {
  const id = data.id;
  return axios.put('/tenants/' + id, data);
}

export function getConfigs() {
  return axios.get('/configs');
}

export function getWorkflows() {
  return axios.get('/tenants/workflows');
}

export function getDomains() {
  return axios.get('/tenants/domains');
}

export function generateTenantSamlResponse(data) {
  return axios.post('/tenants/saml-response', data);
}

//#region Export templates
export function getExportTemplates() {
  return axios.get('/export/templates');
}

export function getExportTemplate(id) {
  return axios.get('/export/template/' + id);
}

export function insertExportTemplate(item) {
  return axios.post('/export/templates', item);
}

export function deleteExportTemplate(item) {
  return axios.delete('/export/template/' + item.id);
}

export function updateExportTemplate(item) {
  return axios.put('/export/template/' + item.id, item);
}

export function saveExportTemplatesOrder(items) {
  return axios.put('/export/templates/order', items);
}

//#endregion

export function login(email, password) {
  return axios.post('/login', { email, password });
}

export function twoFactor(code) {
  return axios.post('/login/two-factor', { code });
}

export function resendSms() {
  return axios.post('/login/resend-sms');
}

export function requestOTPSecret(password) {
  return axios.post('/user/otp-secret', { password });
}

export function enableAuthenticator(code) {
  return axios.put('/user/enable-authenticator', { code });
}

export function disableAuthenticator(password) {
  return axios.put('/user/disable-authenticator', { password });
}

export function updatePassword(currentPassword, newPassword) {
  return axios.put('/user/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

export function getProfile() {
  return axios.get('/user');
}

//#region Rules
export function getOperators() {
  return axios.get('/rules/operators');
}

export function getRuleOutputNames() {
  return axios.get('/rules/output-names');
}

export function getRuleSections(version, rules = false) {
  const query = rules ? '?rules=1&compact=1' : '';
  return axios.get('/version/' + version.id + '/rules/sections' + query);
}

export function getRule(version, id) {
  return axios.get('/version/' + version.id + '/rule/' + id);
}

export function getRules(version) {
  return axios.get('/version/' + version.id + '/rules');
}

export function insertRuleSection(version, section) {
  return axios.post('/version/' + version.id + '/rules/sections', section);
}

export function updateRuleSection(version, section) {
  return axios.put(
    '/version/' + version.id + '/rules/section/' + section.id,
    section
  );
}

export function saveRuleSectionsOrder(version, sections) {
  return axios.put(
    '/version/' + version.id + '/rules/sections/order',
    sections
  );
}

export function saveRulesOrder(version, section, rules) {
  return axios.put(
    '/version/' + version.id + '/rules/section/' + section.id + '/order',
    rules
  );
}

export function getRulesSection(version, id) {
  return axios.get('/version/' + version.id + '/rules/section/' + id);
}

export function deleteRulesSection(version, section) {
  return axios.delete(
    '/version/' + version.id + '/rules/section/' + section.id
  );
}

export function updateRule(version, rule) {
  return axios.put('/version/' + version.id + '/rule/' + rule.id, rule);
}

export function insertRule(version, rule) {
  return axios.post('/version/' + version.id + '/rules', rule);
}

export function deleteRule(version, rule) {
  return axios.delete('/version/' + version.id + '/rule/' + rule.id);
}

//#endregion

export function getExternalInputs() {
  return axios.get('/resources/external-inputs');
}

export function getSpecialConditions() {
  return axios.get('/resources/special-conditions');
}

export function getTranslationPlaceholders() {
  return axios.get('/resources/placeholders');
}

export function getSpecialQuestionTypes() {
  return axios.get('/resources/special-question-types');
}

//#region Questions
export function getQuestionsSection(version, id) {
  return axios.get('/version/' + version.id + '/questions/section/' + id);
}

export function insertQuestionSection(version, section) {
  return axios.post('/version/' + version.id + '/questions/sections', section);
}

export function updateQuestionSection(version, section) {
  return axios.put(
    '/version/' + version.id + '/questions/section/' + section.id,
    section
  );
}

export function deleteQuestionsSection(version, section) {
  return axios.delete(
    '/version/' + version.id + '/questions/section/' + section.id
  );
}

export function getQuestionSections(version) {
  return axios.get('/version/' + version.id + '/questions/sections');
}

export function saveQuestionSectionsOrder(version, sections) {
  return axios.put(
    '/version/' + version.id + '/questions/sections/order',
    sections
  );
}

export function getQuestions(version) {
  return axios.get('/version/' + version.id + '/questions');
}

export function getQuestion(version, id) {
  return axios.get('/version/' + version.id + '/question/' + id);
}

export function updateQuestion(version, question) {
  return axios.put(
    '/version/' + version.id + '/question/' + question.id,
    question
  );
}

export function insertQuestion(version, question) {
  return axios.post('/version/' + version.id + '/questions', question);
}

export function deleteQuestion(version, question) {
  return axios.delete('/version/' + version.id + '/question/' + question.id);
}

export function saveQuestionsOrder(version, section, questions) {
  return axios.put(
    '/version/' + version.id + '/questions/section/' + section.id + '/order',
    questions
  );
}

//#endregion

export function getVersion(id) {
  return axios.get('/version/' + id);
}

export function evaluateToTable(
  tenantId,
  reportLocale,
  patientLocale,
  versionIds,
  showQuestionTable,
  showReportTable,
  showRuleTable
) {
  return axios.post('/validate/evaluate-table', {
    tenant_id: tenantId,
    report_locale: reportLocale,
    patient_locale: patientLocale,
    versions: versionIds,
    question_table: showQuestionTable,
    report_table: showReportTable,
    rule_table: showRuleTable,
  });
}

export async function downloadTable(
  tenantId,
  reportLocale,
  patientLocale,
  versionIds,
  showQuestionTable,
  showReportTable,
  showRuleTable
) {
  // Note: if we don't use responseType: arraybuffer, the data is interpreted
  // as UTF-8 which may lead to corruption.
  const response = await axios.post(
    '/validate/download-table',
    {
      tenant_id: tenantId,
      report_locale: reportLocale,
      patient_locale: patientLocale,
      versions: versionIds,
      question_table: showQuestionTable,
      report_table: showReportTable,
      rule_table: showRuleTable,
    },
    { responseType: 'arraybuffer' }
  );
  downloadFile(response, 'evaluation.xlsx');
}

export function analyzeVersion(versionId) {
  return axios.post('/validate/analyze', {
    version_id: versionId,
  });
}

export function getTemplateSeparators() {
  return axios.get('/resources/template-separators');
}

export function getReportColumnTypes() {
  return axios.get('/resources/report-column-types');
}

export function getSupportedLanguages() {
  return axios.get('/resources/supported-languages');
}

//#region Template sections
export function getTemplatesSection(version, id) {
  return axios.get('/version/' + version.id + '/templates/section/' + id);
}

export function getTemplateSections(version) {
  return axios.get('/version/' + version.id + '/templates/sections');
}

export function updateTemplateSection(version, section) {
  return axios.put(
    '/version/' + version.id + '/templates/section/' + section.id,
    section
  );
}

export function deleteTemplatesSection(version, section) {
  return axios.delete(
    '/version/' + version.id + '/templates/section/' + section.id
  );
}

export function insertTemplateSection(version, section) {
  return axios.post('/version/' + version.id + '/templates/sections', section);
}

export function saveTemplateSectionsOrder(version, sections) {
  return axios.put(
    '/version/' + version.id + '/templates/sections/order',
    sections
  );
}

//#endregion

//#region Templates
export function getTemplate(version, id) {
  return axios.get('/version/' + version.id + '/template/' + id);
}

export function updateTemplate(version, template) {
  return axios.put(
    '/version/' + version.id + '/template/' + template.id,
    template
  );
}

export function insertTemplate(version, template) {
  return axios.post('/version/' + version.id + '/templates', template);
}

export function deleteTemplate(version, template) {
  return axios.delete('/version/' + version.id + '/template/' + template.id);
}

export function saveTemplatesOrder(version, section, templates) {
  return axios.put(
    '/version/' + version.id + '/templates/section/' + section.id + '/order',
    templates
  );
}

//#endregion

// Lists all workflows for the given environment, both code workflows
// and database feature workflows.
export function listAllWorkflows(domain) {
  return axios.get(`/workflows/definitions/${domain}/all`);
}

export function listQuestionSectionTypes() {
  return axios.get('/resources/question-section-types');
}

//#region File sections
export function getFileSection(id) {
  return axios.get('/files/section/' + id);
}

export function getFileSections() {
  return axios.get('/files/sections');
}

export function insertFileSection(section) {
  return axios.post('/files/sections', section);
}

export function updateFileSection(item) {
  return axios.put('/files/section/' + item.id, item);
}

export function saveFileSectionsOrder(items) {
  return axios.put('/files/sections/order', items);
}

export function deleteFileSection(item) {
  return axios.delete('/files/section/' + item.id);
}

//#endregion
//#region Files
export function saveFileOrder(item, items) {
  return axios.put('/files/order', items);
}

export function getFileReferences(fileId) {
  return axios.get(`/file/${fileId}/references`);
}

export function deleteFile(id) {
  return axios.delete('/file/' + id);
}

//#endregion

export function copyTenant(id) {
  return axios.post(`/tenants/${id}/copy`);
}

export function getKnownEmailTemplateKeys() {
  return axios.get('/email-templates/known-keys');
}

export function getTagsAvailable() {
  return axios.get('/resources/tags-available');
}

export function getRuleOverview(version, id) {
  return axios.get(`/version/${version.id}/rule/${id}/visualize`);
}

export function searchAll(version, query) {
  return axios.get('/version/' + version.id + '/search-all', {
    params: { q: query },
  });
}

//#region Export fields
export function insertExportField(item) {
  return axios.post(
    '/export/template/' + item.export_template_id + '/fields',
    item
  );
}

export function updateExportField(item) {
  return axios.put('/export/field/' + item.id, item);
}

export function deleteExportField(id) {
  return axios.delete('/export/field/' + id);
}

export function saveExportFieldsOrder(item, items) {
  return axios.put('/export/template/' + item.id + '/fields/order', items);
}

//#endregion

export default {
  uploadVersionMigration(title, filecontent, settings = {}) {
    const payload = JSON.parse(filecontent);

    return axios.post('/import/migration', { title, payload, settings });
  },
  downloadVersionMigration(config, version) {
    let filename = config.name + ' v' + version.version + '.json';
    return axios
      .get('/export/version/' + version.id + '/migration', {
        responseType: 'blob',
      })
      .then((response) => downloadFile(response, filename));
  },
  getAllQuestions() {
    return axios.get('/allversions/questions');
  },
  getAllRules() {
    return axios.get('/allversions/rules');
  },
  getExportTemplate,
  getExportField(id) {
    return axios.get('/export/field/' + id);
  },

  exportQuestionsPdf(version, languageCode = 'nl') {
    return axios
      .get('/export/version/' + version.id + `/questions/pdf/${languageCode}`, {
        responseType: 'blob',
      })
      .then(downloadFile);
  },

  duplicateExportTemplate(exportTemplateId, name) {
    return axios.post(`/export/template/${exportTemplateId}/duplicate`, {
      name,
    });
  },

  exportQuestionsExcel(version, languageCode = 'nl') {
    return axios
      .get(
        '/export/version/' + version.id + `/questions/excel/${languageCode}`,
        { responseType: 'blob' }
      )
      .then(downloadFile);
  },
  createConfig(data) {
    return axios.post('/configs', data);
  },
  deleteConfig(id) {
    return axios.post('/config/' + id + '/hide');
  },
  duplicateConfig(id, data) {
    return axios.post('/version/' + id + '/duplicate', data);
  },
  getChanges(id) {
    return axios.get('/version/' + id + '/changes');
  },
  closeVersion(id, message) {
    return axios.put('/version/' + id + '/close', { message });
  },

  undoQuestion(version, question) {
    return axios.post(
      '/version/' + version.id + '/question/' + question.id + '/undo'
    );
  },
  undoQuestionSection(version, section) {
    return axios.post(
      '/version/' + version.id + '/questions/section/' + section.id + '/undo'
    );
  },
  undoRule(version, rule) {
    return axios.post('/version/' + version.id + '/rule/' + rule.id + '/undo');
  },
  undoRuleSection(version, section) {
    return axios.post(
      '/version/' + version.id + '/rules/section/' + section.id + '/undo'
    );
  },
  undoTemplate(version, template) {
    return axios.post(
      '/version/' + version.id + '/template/' + template.id + '/undo'
    );
  },
  undoTemplateSection(version, section) {
    return axios.post(
      '/version/' + version.id + '/templates/section/' + section.id + '/undo'
    );
  },

  getTagsAvailable,

  getFile(id) {
    return axios.get('/file/' + id);
  },
  getFiles() {
    return axios.get('/files');
  },
  updateFile(file) {
    return axios.put('/file/' + file.id, file);
  },
  insertFile(file) {
    return axios.post('/files', file);
  },
};
